import { Col } from 'antd';
import EmptyData from 'components/common/EmptyData';
import { INPUT_TYPE } from 'components/common/Input/types';
import DataCard from 'components/DataCard';
import { DEFAULT_FORMAT_DATE_US } from 'constants/days';
import { FormType } from 'constants/formTypes';
import useFormikRefHook from 'hooks/useFormikRefHook';
import { get } from 'lodash';
import moment from 'moment';
import React, { ReactElement, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import SectionHeader from 'routes/Transaction/components/TransactionDetailTab/TransactionForm/SectionHeader';
import { CREATE_DOCUMENT, DELETE_DOCUMENT, UPDATE_DOCUMENT } from 'services/graphql/mutations/documents';
import { IBondDocumentDetails, IDocumentType } from 'types/user';
import { getFormattedDate } from 'utils';
import { formatMessage } from 'utils/IntlMessages';
import * as Yup from 'yup';

import { DataCardActionType, ExtraFieldStatus, IDataCardAction } from '../NotaryCommissions/types';
import NotaryFileUploadTemplate from '../NotaryFileUploadTemplate';
import ExtraField from '../NotaryFileUploadTemplate/ExtraField';
import RecordModal from '../NotaryFileUploadTemplate/RecordModal';
import { InputProps, ITabProps, NotaryDocumentType } from '../types';
import { ErrorMessageTextStyled } from './styled';
import { bondTypeLabel, BondTypeValue } from './types';

const BondFormSchema = {
  company: Yup.string().required('Issuing company is required'),
  policyNo: Yup.string().required('Policy number is required'),
  policyEffectiveDate: Yup.array()
    .length(2, 'Need both start and end date')
    .required('Policy effective date is required'),
  files: Yup.array().min(1, 'Need at least 1 document').required('Need at least 1 document'),
};

// BOND AND INSURANCE
const BondAndInsurance: React.FC<ITabProps> = ({ user, refetchUserData }) => {
  const intl = useIntl();
  const { id } = user;

  const [formType, setFormType] = useState<FormType | undefined>();
  const [chosenBondType, setChosenBondType] = useState<BondTypeValue | undefined>();
  const [chosenBond, setChosenBond] = useState<any>();

  const documents = get(user, 'documents') || [];
  useFormikRefHook({
    name: 'bondAndInsurance',
  });
  const formFields: InputProps[] = useMemo(() => {
    const bondTypeObject: any = chosenBondType
      ? [
          {
            label: 'Type',
            name: 'bondType',
            defaultValue: chosenBondType,
            type: INPUT_TYPE.TEXT_ONLY,
            inputSettings: { show: true, displayValue: bondTypeLabel[chosenBondType] },
          },
        ]
      : [];
    return [
      ...bondTypeObject,
      {
        label: 'Issuing Company',
        name: 'company',
        sideFields: [
          {
            label: 'Policy Number',
            name: 'policyNo',
            size: 12,
          },
        ],
      },
      // must be named "files" to work with the template
      { label: 'Policy Effective Date', name: 'policyEffectiveDate', type: INPUT_TYPE.RANGE_PICKER },
      {
        label: 'Insurance/Bond Certificates',
        name: 'files',
        type: INPUT_TYPE.FILE,
        inputSettings: {
          accept: 'image/*, .pdf',
        },
      },
    ];
  }, [chosenBondType]);

  const detailsFields = ['company', 'bondType', 'policyNo', 'policyEffectiveDate'];

  const closeModal = () => {
    setFormType(undefined);
  };

  const getEffectiveDateInfo = (policyStartDate: string, policyExpireDate: string): any[] => {
    const effectiveStartDayTime = new Date(policyStartDate).getTime();
    const effectiveExpiredDayTime = new Date(policyExpireDate).getTime();
    const currentTime = Date.now();
    let policyEffectiveDateStatus: ExtraFieldStatus;
    let tooltip: string;
    let color: string;
    if (currentTime - effectiveExpiredDayTime > 0) {
      policyEffectiveDateStatus = ExtraFieldStatus.DANGER;
      tooltip = 'Expired';
      color = 'red';
    } else if (effectiveStartDayTime > currentTime) {
      policyEffectiveDateStatus = ExtraFieldStatus.PENDING;
      tooltip = 'Pending';
      color = '#F7CB73';
    } else {
      policyEffectiveDateStatus = ExtraFieldStatus.ACTIVE;
      tooltip = 'Active';
      color = '#10893E';
    }
    return [policyEffectiveDateStatus, tooltip, color];
  };

  const generateBondAndInsuranceCards = (bondData: IDocumentType<IBondDocumentDetails>): ReactElement => {
    const { details, id: bondId, files } = bondData;
    const thumbnail = files.map((f) => f.thumbnailPipeFrom || f.pipeFrom);

    const { policyEffectiveDate, policyNo, company, bondType } = details;
    const policyStartDate = moment(getFormattedDate(get(policyEffectiveDate, '0', ''))[0]).format(
      DEFAULT_FORMAT_DATE_US,
    );
    const policyExpireDate = moment(getFormattedDate(get(policyEffectiveDate, '1', ''))[0]).format(
      DEFAULT_FORMAT_DATE_US,
    );
    const [policyEffectiveDateStatus, cardTooltip, tooltipColor] = getEffectiveDateInfo(
      policyStartDate,
      policyExpireDate,
    );

    // let status: ExtraFieldStatus;
    // switch(status) {
    //   case ExtraFieldStatus.ACTIVE:
    //     status =
    // }
    const extraFields = [
      <ExtraField labelId='userProfile.bondAndInsurance.policyNumber' data={policyNo} />,
      <ExtraField labelId='userProfile.bondAndInsurance.policyEffectiveDate.start' data={policyStartDate} />,
      <ExtraField
        status={policyEffectiveDateStatus}
        labelId='userProfile.bondAndInsurance.policyEffectiveDate.expired'
        data={policyExpireDate}
        tooltip={policyEffectiveDateStatus === ExtraFieldStatus.DANGER ? 'Expired' : ''}
      />,
    ];

    const filesValueForInput = files.map((f) => ({
      uid: f.id,
      status: 'done',
      name: f.name,
      url: f.pipeFrom,
    }));

    const action: IDataCardAction = {
      type: DataCardActionType.INTERACT,
      edit: () => {
        // todo: show edit comm form
        setChosenBondType(bondType);
        setFormType(FormType.EDIT);
        setChosenBond(() => ({
          ...details,
          id: bondId,
          files: filesValueForInput,
        }));
      },
      delete: () => {
        // todo: show confirmation box
        setFormType(FormType.DELETE);
        setChosenBond(bondData);
      },
    };

    return (
      <Col key={bondId} span={24} xl={12} style={{ minWidth: 400 }}>
        <DataCard
          style={{ minWidth: 400, maxWidth: '100%' }}
          mobileWidth='280px'
          mobileHeight='auto'
          responsive
          mobileThreshold={630}
          avatar={thumbnail}
          avatarSizes={{ width: '150px' }}
          avatarSizesMobile={{ height: '120px' }}
          main={<div className='gx-font-weight-medium gx-mb-2'>{company}</div>}
          extra={extraFields}
          action={action}
          height='200px'
          status={policyEffectiveDateStatus}
          statusTooltip={cardTooltip}
          statusTooltipColor={tooltipColor}
        />
      </Col>
    );
  };

  const { CarInsuranceSection, EoInsuranceSection, BondSection } = useMemo(
    () =>
      documents.reduce(
        (acc, document) => {
          if (document.type !== NotaryDocumentType.BOND || !document?.details?.bondType) return acc;

          switch (document.details.bondType) {
            case BondTypeValue.carinsurance:
              acc.CarInsuranceSection.push(generateBondAndInsuranceCards(document));
              break;
            case BondTypeValue.eoinsurance:
              acc.EoInsuranceSection.push(generateBondAndInsuranceCards(document));
              break;
            case BondTypeValue.bond:
              acc.BondSection.push(generateBondAndInsuranceCards(document));
              break;
            default:
              break;
          }
          return acc;
        },
        { CarInsuranceSection: [], EoInsuranceSection: [], BondSection: [] } as Record<string, ReactElement[]>,
      ),
    [documents],
  );

  const isBondSectionEmpty = BondSection.length === 0;
  const isCarInsuranceSectionEmpty = CarInsuranceSection.length === 0;
  const isEoInsuranceSectionEmpty = EoInsuranceSection.length === 0;

  return (
    <div className=''>
      <RecordModal
        refetchUserData={refetchUserData}
        formType={formType}
        record={chosenBond}
        closeModal={closeModal}
        userId={id}
        formTitle={
          formType && formType !== FormType.DELETE
            ? formatMessage(intl, `userProfile.bondAndInsurance.${formType}`)
            : ''
        }
        updateMutation={{ mutation: UPDATE_DOCUMENT, key: 'updateDocumentEntity' }}
        createMutation={{ mutation: CREATE_DOCUMENT, key: 'createDocumentEntity' }}
        deleteMutation={DELETE_DOCUMENT}
        formFields={formFields}
        detailsFields={detailsFields}
        documentType={NotaryDocumentType.BOND}
        validationSchema={BondFormSchema}
      />
      <SectionHeader
        buttonTitle='+ Car Insurance Document'
        onHeaderButtonClick={() => {
          setChosenBondType(BondTypeValue.carinsurance);
          setFormType(FormType.ADD);
        }}
        ErrorComponent={
          isCarInsuranceSectionEmpty ? (
            <ErrorMessageTextStyled>Need at least one Car Insurance document</ErrorMessageTextStyled>
          ) : undefined
        }>
        Car Insurance
      </SectionHeader>
      <div>
        <NotaryFileUploadTemplate RecordCards={CarInsuranceSection} />
        {isCarInsuranceSectionEmpty && <EmptyData />}
      </div>
      <SectionHeader
        separatorPosition='top'
        buttonTitle='+ EO Insurance Document'
        onHeaderButtonClick={() => {
          setChosenBondType(BondTypeValue.eoinsurance);
          setFormType(FormType.ADD);
        }}
        ErrorComponent={
          isEoInsuranceSectionEmpty ? (
            <ErrorMessageTextStyled>Need at least one EO Insurance document</ErrorMessageTextStyled>
          ) : undefined
        }>
        Errors and Omissions Insurance
      </SectionHeader>
      <div>
        <NotaryFileUploadTemplate RecordCards={EoInsuranceSection} />
        {isEoInsuranceSectionEmpty && <EmptyData />}
      </div>
      <SectionHeader
        separatorPosition='top'
        buttonTitle='+ Bond Document'
        onHeaderButtonClick={() => {
          setChosenBondType(BondTypeValue.bond);
          setFormType(FormType.ADD);
        }}
        ErrorComponent={
          isBondSectionEmpty ? (
            <ErrorMessageTextStyled>Need at least one Bond document</ErrorMessageTextStyled>
          ) : undefined
        }>
        Bond
      </SectionHeader>
      <div>
        <NotaryFileUploadTemplate RecordCards={BondSection} />
        {isBondSectionEmpty && <EmptyData />}
      </div>
    </div>
  );
};

export default React.memo(BondAndInsurance);
